import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { useAuth } from '../context/AuthContext';
import { attendanceService } from '../services/attendanceService';
import { leaveService } from '../services/leaveService';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/employeeService';
import { AttendanceSummary, LeaveBalance, PayrollRecord, Employee, MarkAttendanceData, AttendanceStatus } from '../types';
import { Select } from '../components/common/Select';
import toast from 'react-hot-toast';

export function Dashboard() {
  const { user, isHR } = useAuth();
  const [attendanceSummary, setAttendanceSummary] = useState<AttendanceSummary | null>(null);
  const [companySummary, setCompanySummary] = useState<any>(null);
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalance | null>(null);
  const [latestPayroll, setLatestPayroll] = useState<PayrollRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Admin attendance marking state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [attendanceStatus, setAttendanceStatus] = useState<AttendanceStatus>('PRESENT');
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  
  // Employee list modal state
  const [showEmployeeList, setShowEmployeeList] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('');
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();

        if (isHR) {
          // For admin/HR: fetch company-wide summary
          const [companyData, payrollRecords] = await Promise.all([
            attendanceService.getCompanyAttendanceSummary(month, year),
            payrollService.getMyPayrollRecords(),
          ]);

          setCompanySummary(companyData || null);
          setLatestPayroll(Array.isArray(payrollRecords) && payrollRecords.length > 0 ? payrollRecords[0] : null);
        } else {
          // For employees: fetch personal summary
          const [summary, balance, payrollRecords] = await Promise.all([
            attendanceService.getAttendanceSummary(month, year),
            leaveService.getMyLeaveBalance(month, year),
            payrollService.getMyPayrollRecords(),
          ]);

          setAttendanceSummary(summary || null);
          setLeaveBalance(balance || null);
          setLatestPayroll(Array.isArray(payrollRecords) && payrollRecords.length > 0 ? payrollRecords[0] : null);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default values on error to prevent crashes
        setAttendanceSummary(null);
        setCompanySummary(null);
        setLeaveBalance(null);
        setLatestPayroll(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    
    // Fetch employees for admin attendance marking
    if (isHR) {
      fetchEmployees();
    }
  }, [isHR]);
  
  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAll(1, 100, { isActive: true });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };
  
  const handleMarkEmployeeAttendance = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    
    setIsMarkingAttendance(true);
    try {
      // Use today's date
      const today = new Date().toISOString().split('T')[0];
      const attendanceData: MarkAttendanceData = {
        date: today,
        status: attendanceStatus,
      };
      
      await attendanceService.markAttendanceForEmployee(selectedEmployee, attendanceData);
      toast.success('Attendance marked successfully!');
      
      // Reset form
      setSelectedEmployee(null);
      setAttendanceStatus('PRESENT');
      
      // Refresh dashboard data
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const companyData = await attendanceService.getCompanyAttendanceSummary(month, year);
      setCompanySummary(companyData);
      
      // Refresh employee list if modal is open
      if (showEmployeeList && selectedStatusFilter) {
        await fetchEmployeesByStatus(selectedStatusFilter);
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsMarkingAttendance(false);
    }
  };

  const fetchEmployeesByStatus = async (status: string) => {
    setIsLoadingEmployees(true);
    try {
      console.log('Fetching employees with status:', status);
      const employees = await attendanceService.getEmployeesByStatus(status);
      console.log('Fetched employees:', employees);
      setFilteredEmployees(employees);
    } catch (error) {
      console.error('Error fetching employees by status:', error);
      toast.error('Failed to fetch employees');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const handleCardClick = async (statName: string) => {
    if (!isHR) return; // Only for admin/HR
    
    console.log('Card clicked:', statName);
    
    let statusFilter = '';
    if (statName === 'Employees Present') {
      statusFilter = 'PRESENT';
    } else if (statName === 'Employees Absent') {
      statusFilter = 'ABSENT_OR_UNPAID';
    } else if (statName === 'On Casual Leave') {
      statusFilter = 'LEAVE_CL';
    } else if (statName === 'On Period Leave') {
      statusFilter = 'LEAVE_PL';
    }
    
    console.log('Status filter:', statusFilter);
    
    if (statusFilter) {
      setSelectedStatusFilter(statusFilter);
      setShowEmployeeList(true);
      await fetchEmployeesByStatus(statusFilter);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  // Stats for admin/HR (company-wide)
  const companyStats = companySummary ? [
    {
      name: 'Employees Present',
      value: companySummary.employeesPresent || 0,
      subtitle: `out of ${companySummary.totalEmployees || 0}`,
      icon: CheckCircleIcon,
      color: 'text-success-600',
      bgColor: 'bg-success-50',
    },
    {
      name: 'On Casual Leave',
      value: companySummary.employeesOnCL || 0,
      subtitle: 'employees',
      icon: CalendarIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      name: 'On Period Leave',
      value: companySummary.employeesOnPL || 0,
      subtitle: 'employees',
      icon: ClockIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      name: 'Employees Absent',
      value: companySummary.employeesAbsent || 0,
      subtitle: `out of ${companySummary.totalEmployees || 0}`,
      icon: ExclamationCircleIcon,
      color: 'text-danger-600',
      bgColor: 'bg-danger-50',
    },
  ] : [];

  // Stats for employees (personal)
  const employeeStats = [
    {
      name: 'Present Days',
      value: attendanceSummary?.present || 0,
      subtitle: '',
      icon: CheckCircleIcon,
      color: 'text-success-600',
      bgColor: 'bg-success-50',
    },
    {
      name: 'Leave Days',
      value: (attendanceSummary?.leaveCL || 0) + (attendanceSummary?.leavePL || 0),
      subtitle: '',
      icon: ClockIcon,
      color: 'text-warning-600',
      bgColor: 'bg-warning-50',
    },
    {
      name: 'Unpaid Leave',
      value: attendanceSummary?.leaveUnpaid || 0,
      subtitle: '',
      icon: ExclamationCircleIcon,
      color: 'text-danger-600',
      bgColor: 'bg-danger-50',
    },
    {
      name: 'CL Balance',
      value: leaveBalance?.casualLeave?.balance || 0,
      subtitle: '',
      icon: CalendarIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
  ];

  const stats = isHR ? companyStats : employeeStats;

  return (
    <div className="space-y-6 animate-in">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">
          Welcome back, {user?.employee?.firstName || user?.email?.split('@')[0] || 'User'}! 👋
        </h1>
        <p className="text-primary-100 mt-1">
          {isHR 
            ? 'Company-wide attendance overview and management dashboard.'
            : "Here's what's happening with your attendance and payroll."}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card 
            key={stat.name} 
            className="flex items-center gap-4"
            onClick={isHR ? () => handleCardClick(stat.name) : undefined}
          >
            <div className={`p-3 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{stat.name}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              {stat.subtitle && (
                <p className="text-xs text-gray-400 mt-0.5">{stat.subtitle}</p>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 ${!isHR ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6`}>
        {/* Leave Balance Card - Only for employees */}
        {!isHR && (
          <Card>
          <CardHeader
            title="Leave Balance"
            subtitle={`${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
            action={
              <Link to="/leave">
                <Button variant="outline" size="sm">Apply Leave</Button>
              </Link>
            }
          />
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Casual Leave (CL)</p>
                <p className="text-lg font-semibold">
                  {leaveBalance?.casualLeave?.balance || 0} / {leaveBalance?.casualLeave?.entitled || 0} days
                </p>
              </div>
              <div className="w-16 h-16">
                <svg viewBox="0 0 36 36" className="w-full h-full">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    strokeDasharray={`${((leaveBalance?.casualLeave?.balance || 0) / (leaveBalance?.casualLeave?.entitled || 1)) * 100}, 100`}
                  />
                </svg>
              </div>
            </div>
            {leaveBalance && leaveBalance.paidLeave?.entitled > 0 && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-orange-200">
                <div>
                  <p className="text-sm text-gray-500">Period Leave (PL)</p>
                  <p className="text-lg font-semibold">
                    {leaveBalance.paidLeave?.balance || 0} / {leaveBalance.paidLeave?.entitled || 0} days
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Cannot be carried forward - lapses if unused
                  </p>
                </div>
                <div className="w-16 h-16">
                  <svg viewBox="0 0 36 36" className="w-full h-full">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="3"
                      strokeDasharray={`${((leaveBalance.paidLeave?.balance || 0) / (leaveBalance.paidLeave?.entitled || 1)) * 100}, 100`}
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
          </Card>
        )}

        {/* Latest Payroll Card */}
        <Card>
          <CardHeader
            title="Latest Payroll"
            subtitle={latestPayroll ? `${new Date(latestPayroll.year, latestPayroll.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'No payroll records'}
            action={
              <Link to="/payroll">
                <Button variant="outline" size="sm">View All</Button>
              </Link>
            }
          />
          {latestPayroll ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Gross Salary</span>
                <span className="font-semibold">₹{(latestPayroll.grossSalary || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Deductions</span>
                <span className="font-semibold text-danger-600">
                  -₹{(latestPayroll.totalDeductions || 0).toLocaleString()}
                </span>
              </div>
              <div className="border-t pt-4 flex items-center justify-between">
                <span className="text-lg font-semibold">Net Salary</span>
                <span className="text-2xl font-bold text-success-600">
                  ₹{(latestPayroll.netSalary || 0).toLocaleString()}
                </span>
              </div>
              {latestPayroll.payslipPath && (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={async () => {
                    try {
                      const blob = await payrollService.downloadPayslip(latestPayroll.id);
                      
                      if (!blob || blob.size === 0) {
                        toast.error('Payslip file is empty or invalid');
                        return;
                      }
                      
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `payslip_${latestPayroll.month}_${latestPayroll.year}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                      toast.success('Payslip downloaded successfully');
                    } catch (error: any) {
                      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to download payslip';
                      toast.error(errorMessage);
                      console.error('Error downloading payslip:', error);
                    }
                  }}
                >
                  <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                  Download Payslip
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CurrencyDollarIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No payroll records available yet.</p>
            </div>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/attendance">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-center">
              <CalendarIcon className="w-8 h-8 mx-auto text-primary-600 mb-2" />
              <p className="font-medium">Mark Attendance</p>
            </div>
          </Link>
          <Link to="/leave">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-center">
              <ClockIcon className="w-8 h-8 mx-auto text-primary-600 mb-2" />
              <p className="font-medium">Apply Leave</p>
            </div>
          </Link>
          <Link to="/payroll">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-center">
              <CurrencyDollarIcon className="w-8 h-8 mx-auto text-primary-600 mb-2" />
              <p className="font-medium">View Payslips</p>
            </div>
          </Link>
          <Link to="/profile">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-center">
              <ArrowTrendingUpIcon className="w-8 h-8 mx-auto text-primary-600 mb-2" />
              <p className="font-medium">Update Profile</p>
            </div>
          </Link>
        </div>
      </Card>

      {/* HR Admin Section */}
      {isHR && (
        <>
          <Card>
            <CardHeader title="HR Admin Actions" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/employees">
                <Button variant="outline" className="w-full">Manage Employees</Button>
              </Link>
              <Link to="/reports">
                <Button variant="outline" className="w-full">Run Payroll</Button>
              </Link>
              <Link to="/attendance">
                <Button variant="outline" className="w-full">Bulk Import</Button>
              </Link>
              <Link to="/leave">
                <Button variant="outline" className="w-full">Approve Leaves</Button>
              </Link>
            </div>
          </Card>

          {/* Mark Employee Attendance */}
          <Card>
            <CardHeader title="Mark Employee Attendance (Today)" />
            <div className="space-y-4">
              <Select
                label="Select Employee"
                value={selectedEmployee ? String(selectedEmployee) : ''}
                onChange={(e) => setSelectedEmployee(e.target.value ? parseInt(e.target.value) : null)}
                options={[
                  { value: '', label: 'Select an employee...' },
                  ...employees.map(emp => ({
                    value: String(emp.id),
                    label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                  })),
                ]}
              />
              
              <Select
                label="Status"
                value={attendanceStatus}
                onChange={(e) => setAttendanceStatus(e.target.value as AttendanceStatus)}
                options={[
                  { value: 'PRESENT', label: 'Present' },
                  { value: 'PRESENT_LATE', label: 'Present but Late' },
                  { value: 'ABSENT', label: 'Absent' },
                  { value: 'HALF_DAY', label: 'Half Day' },
                  { value: 'LEAVE_CL', label: 'Casual Leave' },
                  { value: 'LEAVE_PL', label: 'Period Leave' },
                  { value: 'LEAVE_UNPAID', label: 'Unpaid Leave' },
                ]}
              />
              
              <Button
                onClick={handleMarkEmployeeAttendance}
                isLoading={isMarkingAttendance}
                disabled={!selectedEmployee}
                className="w-full"
              >
                Mark Attendance
              </Button>
              <p className="text-sm text-gray-500 text-center">
                Attendance will be marked for today: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </Card>
        </>
      )}

      {/* Employee List Modal */}
      {showEmployeeList && isHR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader 
              title={
                selectedStatusFilter === 'PRESENT' ? 'Employees Present' :
                selectedStatusFilter === 'ABSENT_OR_UNPAID' ? 'Employees Absent' :
                selectedStatusFilter === 'LEAVE_CL' ? 'On Casual Leave' :
                selectedStatusFilter === 'LEAVE_PL' ? 'On Period Leave' :
                'Employees'
              }
              action={
                <button
                  onClick={() => {
                    setShowEmployeeList(false);
                    setFilteredEmployees([]);
                    setSelectedStatusFilter('');
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              }
            />
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {isLoadingEmployees ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No employees found for this status.</p>
                  <p className="text-xs mt-2 text-gray-400">Status: {selectedStatusFilter}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 mb-2">
                    Found {filteredEmployees.length} employee(s)
                  </p>
                  {filteredEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {employee.employeeCode} • {employee.department || 'N/A'}
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-sm text-gray-500 truncate">{employee.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
